import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import {
  guardedTransaction,
  getOrderIdWithPaypal,
  initiatePrivileged,
  initiatePrivilegedWithPaypal,
  initiateSpecWithPaypal,
  listingPurchaseAvailability,
  processTransactionSmsNotifications,
  sendMarketplaceMessage,
  transitionPrivileged,
  transitionPrivilegedWithPaypal,
} from '../../util/api';
import { applyPurchaseAvailabilityToListings } from '../../util/purchaseAvailability';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError, isNetworkError } from '../../util/errors';
import * as log from '../../util/log';
import { setCurrentUserHasOrders, fetchCurrentUser } from '../../ducks/user.duck';
import { shippoDebugError, shippoDebugLog } from '../../util/shippoLabelDebug';
import { StatusCode } from '../../util/enums';

const NON_CRITICAL_CHECKOUT_STATUSES = new Set([
  StatusCode.PAYMENT_REQUIRED,
  StatusCode.CONFLICT,
  StatusCode.NOT_FOUND,
]);

const TRANSITION_REQUEST_PAYMENT_WITH_PAYPAL = 'transition/request-payment-with-paypal';
const TRANSITION_REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL =
  'transition/request-payment-after-inquiry-with-paypal';
const TRANSITION_REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL =
  'transition/request-payment-after-offer-with-paypal';

const triggerTransactionSmsNotifications = ({ transactionId, source, transition }) => {
  if (!transactionId) {
    return;
  }

  processTransactionSmsNotifications({ transactionId, source, transition }).catch(e => {
    log.warn(e, 'transaction-sms-direct-trigger-failed', {
      source,
      transactionId: transactionId?.uuid || transactionId,
      transition,
    });
  });
};
const PAYPAL_REQUEST_TRANSITIONS = [
  TRANSITION_REQUEST_PAYMENT_WITH_PAYPAL,
  TRANSITION_REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL,
  TRANSITION_REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL,
];
const isPayPalRequestTransition = transitionName =>
  PAYPAL_REQUEST_TRANSITIONS.includes(transitionName);

const summarizeCheckoutContext = ({
  extraParams = {},
  orderParams = {},
  processAlias = null,
  transactionId = null,
  transitionName = null,
}) => {
  const protectedData = orderParams?.protectedData || {};
  const shippingDetails = protectedData?.shippingDetails || orderParams?.shippingDetails || {};
  const carrierParams = orderParams?.carrierParams || {};

  return {
    cartItemCount: Array.isArray(orderParams?.cartItems) ? orderParams.cartItems.length : 0,
    deliveryMethod: orderParams?.deliveryMethod || protectedData?.deliveryMethod || null,
    hasShippingAddress: Boolean(
      shippingDetails?.address?.line1 || shippingDetails?.line1 || shippingDetails?.street1
    ),
    listingId: orderParams?.listingId?.uuid || orderParams?.listingId || null,
    paymentMethodType:
      protectedData?.paymentProvider ||
      orderParams?.paymentProvider ||
      extraParams?.paymentMethodType ||
      null,
    processAlias,
    selectedShippingMethod:
      carrierParams?.provider ||
      protectedData?.carrier ||
      shippingDetails?.shippingPreference ||
      null,
    shippingRateId:
      carrierParams?.shippingRateId || carrierParams?.rate || protectedData?.rate || null,
    transactionId: transactionId?.uuid || transactionId || null,
    transitionName,
  };
};

// ================ Async thunks ================ //

export const fetchCheckoutValidationListings = ({ ids = [] } = {}) => async (
  _dispatch,
  _getState,
  sdk
) => {
  const normalizedIds = Array.from(
    new Set(
      (ids || [])
        .map(id => (typeof id === 'string' ? id : id?.uuid || id?.id?.uuid || id?.id || null))
        .filter(Boolean)
    )
  );

  if (!normalizedIds.length) {
    return [];
  }

  const MAX_RETRIES = 2;
  const queryWithRetry = async retriesLeft => {
    try {
      return await sdk.listings.query({
        ids: normalizedIds,
        perPage: normalizedIds.length,
        'fields.listing': ['publicData', 'state', 'deleted'],
      });
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  try {
    const [response, availability] = await Promise.all([
      queryWithRetry(MAX_RETRIES),
      listingPurchaseAvailability({ listingIds: normalizedIds }),
    ]);
    return applyPurchaseAvailabilityToListings(response?.data?.data || [], availability);
  } catch (error) {
    throw storableError(error);
  }
};

////////////////////
// Initiate Order //
////////////////////
const initiateOrderPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, rejectWithValue }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    currency,
    quantity,
    bookingDates,
    hasPayoutDetailsSkipped,
    newPaymentMethod,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};
  const walletFlowMaybe =
    isPrivilegedTransition && hasPayoutDetailsSkipped
      ? {
          hasPayoutDetailsSkipped: true,
          ...(newPaymentMethod ? { newPaymentMethod } : {}),
        }
      : {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(currency ? { currency } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
        ...walletFlowMaybe,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
        ...walletFlowMaybe,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    shippoDebugLog('CHECKOUT', 'initiateOrderNetworkSuccess', {
      transactionId: order?.id?.uuid || order?.id || null,
      lastTransition: order?.attributes?.lastTransition || null,
      state: order?.attributes?.state || null,
    });
    triggerTransactionSmsNotifications({
      transactionId: order?.id || transactionId,
      source: 'CheckoutPage.initiateOrder',
      transition: order?.attributes?.lastTransition || transitionName,
    });
    dispatch(setCurrentUserHasOrders());
    return order;
  };

  const handleError = e => {
    const status = e?.status || e?.statusCode || e?.response?.status || null;
    const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);
    const logFn = isNonCritical ? log.warn : log.error;

    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    logFn(e, isNonCritical ? 'initiate-order-failed-non-critical' : 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    shippoDebugError('CHECKOUT', 'initiateOrderNetworkFailed', e, {
      transactionId: transactionIdMaybe?.transactionId || null,
      listingId: orderParams?.listingId?.uuid || orderParams?.listingId || null,
      processAlias,
      transitionName,
      isTransition,
      isPrivilegedTransition,
      rate:
        transitionParams?.carrierParams?.shippingRateId ||
        transitionParams?.carrierParams?.rate ||
        transitionParams?.protectedData?.rate ||
        null,
      carrierParams: transitionParams?.carrierParams || null,
      shippingDetails: transitionParams?.protectedData?.shippingDetails || null,
    });
    return rejectWithValue(storableError(e));
  };

  shippoDebugLog('CHECKOUT', 'initiateOrderNetworkRequest', {
    transactionId: transactionId?.uuid || transactionId || null,
    listingId: orderParams?.listingId?.uuid || orderParams?.listingId || null,
    processAlias,
    transitionName,
    isTransition,
    isPrivilegedTransition,
    deliveryMethod: transitionParams?.deliveryMethod || null,
    rate:
      transitionParams?.carrierParams?.shippingRateId ||
      transitionParams?.carrierParams?.rate ||
      transitionParams?.protectedData?.rate ||
      null,
    hasPayoutDetailsSkipped: Boolean(hasPayoutDetailsSkipped),
    hasNewPaymentMethod: Boolean(newPaymentMethod),
    carrierParams: transitionParams?.carrierParams || null,
    shippingDetails: transitionParams?.protectedData?.shippingDetails || null,
  });

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // All app transitions pass through the server so Vacation Mode cannot be bypassed.
    return guardedTransaction({ action: 'transition', bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // All app initiations pass through the server so Vacation Mode cannot be bypassed.
    return guardedTransaction({ action: 'initiate', bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const initiateOrderThunk = createAsyncThunk(
  'CheckoutPage/initiateOrder',
  initiateOrderPayloadCreator
);
// Backward compatible wrapper function for initiateOrder
export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    initiateOrderThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

////////////////////////////////////
// Create PayPal order id payload //
////////////////////////////////////
const createOrderIdWithPaypalPayloadCreator = (
  { orderParams, transactionId, requestTransition },
  { rejectWithValue }
) => {
  const isTransition = !!transactionId;
  const transitionName =
    requestTransition ||
    (isTransition
      ? TRANSITION_REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL
      : TRANSITION_REQUEST_PAYMENT_WITH_PAYPAL);

  const {
    processAlias,
    merchantId,
    deliveryMethod,
    quantity,
    bookingDates,
    shippingDetails = {},
    ...otherOrderParams
  } = orderParams || {};

  if (!merchantId) {
    const error = new Error('Missing merchantId for PayPal checkout');
    log.warn(error, 'checkout-paypal-merchant-missing', {
      ...summarizeCheckoutContext({
        orderParams,
        transactionId,
        transitionName,
      }),
    });
    return rejectWithValue(storableError(error));
  }

  if (!isTransition && !processAlias) {
    const error = new Error('Missing transaction process alias for PayPal checkout');
    log.warn(error, 'checkout-process-alias-missing', {
      ...summarizeCheckoutContext({
        orderParams,
        transitionName,
      }),
    });
    return rejectWithValue(storableError(error));
  }

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  const payload = {
    merchantId,
    isSpeculative: false,
    orderData: {
      deliveryMethod,
      shippingDetails,
      bookingDates: bookingDates || {},
    },
    bodyParams,
    queryParams,
  };

  shippoDebugLog('CHECKOUT', 'createPayPalOrderRequest', {
    transactionId: transactionId?.uuid || transactionId || null,
    processAlias: processAlias || null,
    transitionName,
    listingId: transitionParams?.listingId?.uuid || transitionParams?.listingId || null,
    merchantId,
  });

  return getOrderIdWithPaypal(payload)
    .then(response => {
      const paypalOrderId = response?.orderData?.params?.protectedData?.paypalOrderId;
      shippoDebugLog('CHECKOUT', 'createPayPalOrderSuccess', {
        transactionId: transactionId?.uuid || transactionId || null,
        transitionName,
        paypalOrderId: paypalOrderId || null,
      });
      return response;
    })
    .catch(e => {
      const status = e?.status || e?.statusCode || e?.response?.status || null;
      const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);
      const logFn = isNonCritical ? log.warn : log.error;

      logFn(
        e,
        isNonCritical
          ? 'create-paypal-order-id-failed-non-critical'
          : 'create-paypal-order-id-failed',
        {
          ...summarizeCheckoutContext({
            orderParams,
            processAlias,
            transactionId,
            transitionName,
          }),
        }
      );
      shippoDebugError('CHECKOUT', 'createPayPalOrderFailed', e, {
        transactionId: transactionId?.uuid || transactionId || null,
        processAlias,
        transitionName,
      });
      return rejectWithValue(storableError(e));
    });
};

export const createOrderIdWithPaypalThunk = createAsyncThunk(
  'CheckoutPage/createOrderIdWithPaypal',
  createOrderIdWithPaypalPayloadCreator
);

export const createOrderIdWithPaypal = (
  orderParams,
  transactionId,
  requestTransition
) => dispatch => {
  return dispatch(
    createOrderIdWithPaypalThunk({
      orderParams,
      transactionId,
      requestTransition,
    })
  ).unwrap();
};

//////////////////////////////////////////
// Initiate/transition PayPal checkout  //
//////////////////////////////////////////
const initiateOrderWithPayPalPayloadCreator = (
  { orderParams, extraParams = {}, transactionId },
  { dispatch, rejectWithValue }
) => {
  const isTransition = !!transactionId;
  const payload = {
    isSpeculative: false,
    orderParams,
    extraParams,
  };

  shippoDebugLog('CHECKOUT', 'initiatePayPalOrderRequest', {
    transactionId: transactionId?.uuid || transactionId || null,
    transition: orderParams?.transition || null,
    listingId: orderParams?.params?.listingId?.uuid || orderParams?.params?.listingId || null,
    paypalOrderId: extraParams?.paypalOrderId || null,
    captureId: extraParams?.captureId || null,
    isTransition,
  });

  const request = isTransition
    ? transitionPrivilegedWithPaypal(payload)
    : initiatePrivilegedWithPaypal(payload);

  return request
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const order = entities?.[0];
      dispatch(setCurrentUserHasOrders());

      shippoDebugLog('CHECKOUT', 'initiatePayPalOrderSuccess', {
        orderId: order?.id?.uuid || order?.id || null,
        lastTransition: order?.attributes?.lastTransition || null,
        state: order?.attributes?.state || null,
      });
      return order;
    })
    .catch(e => {
      const status = e?.status || e?.statusCode || e?.response?.status || null;
      const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);
      const logFn = isNonCritical ? log.warn : log.error;

      logFn(
        e,
        isNonCritical
          ? 'initiate-order-with-paypal-failed-non-critical'
          : 'initiate-order-with-paypal-failed',
        {
          ...summarizeCheckoutContext({
            extraParams,
            orderParams: {
              ...(orderParams?.params || {}),
              ...(orderParams?.orderData || {}),
              protectedData: orderParams?.params?.protectedData,
            },
            transactionId,
            transitionName: orderParams?.transition || null,
          }),
        }
      );
      shippoDebugError('CHECKOUT', 'initiatePayPalOrderFailed', e, {
        transactionId: transactionId?.uuid || transactionId || null,
        transition: orderParams?.transition || null,
      });
      return rejectWithValue(storableError(e));
    });
};

export const initiateOrderWithPayPalThunk = createAsyncThunk(
  'CheckoutPage/initiateOrderWithPayPal',
  initiateOrderWithPayPalPayloadCreator
);

export const initiateOrderWithPayPal = (orderParams, extraParams, transactionId) => dispatch => {
  return dispatch(
    initiateOrderWithPayPalThunk({
      orderParams,
      extraParams,
      transactionId,
    })
  ).unwrap();
};

/////////////////////
// Confirm Payment //
/////////////////////
const confirmPaymentPayloadCreator = (
  { transactionId, transitionName, transitionParams = {} },
  { rejectWithValue }
) => {
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  return guardedTransaction({ action: 'transition', bodyParams, queryParams })
    .then(response => {
      const order = response.data.data;
      shippoDebugLog('CHECKOUT', 'confirmPaymentNetworkSuccess', {
        transactionId: order?.id?.uuid || order?.id || transactionId?.uuid || null,
        lastTransition: order?.attributes?.lastTransition || null,
        state: order?.attributes?.state || null,
      });
      triggerTransactionSmsNotifications({
        transactionId: order?.id || transactionId,
        source: 'CheckoutPage.confirmPayment',
        transition: order?.attributes?.lastTransition || transitionName,
      });
      return order;
    })
    .catch(e => {
      const status = e?.status || e?.statusCode || e?.response?.status || null;
      const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);
      const logFn = isNonCritical ? log.warn : log.error;

      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      logFn(e, isNonCritical ? 'initiate-order-failed-non-critical' : 'initiate-order-failed', {
        ...transactionIdMaybe,
        transitionName,
      });
      shippoDebugError('CHECKOUT', 'confirmPaymentNetworkFailed', e, {
        transactionId: transactionId?.uuid || transactionId || null,
        transitionName,
        transitionParams,
      });
      return rejectWithValue(storableError(e));
    });
};

export const confirmPaymentThunk = createAsyncThunk(
  'CheckoutPage/confirmPayment',
  confirmPaymentPayloadCreator
);
// Backward compatible wrapper function for confirmPayment
export const confirmPayment = (
  transactionId,
  transitionName,
  transitionParams = {}
) => dispatch => {
  shippoDebugLog('CHECKOUT', 'confirmPaymentNetworkRequest', {
    transactionId: transactionId?.uuid || transactionId || null,
    transitionName,
    transitionParams,
  });
  return dispatch(
    confirmPaymentThunk({
      transactionId,
      transitionName,
      transitionParams,
    })
  ).unwrap();
};

//////////////////
// Send Message //
//////////////////
const sendMessagePayloadCreator = ({ message, id: orderId }) => {
  if (message) {
    return sendMarketplaceMessage({
      transactionId: orderId,
      content: message,
      isOrderMessage: true,
      context: {
        source: 'CheckoutPage',
      },
    })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        // We resolve with messageSuccess: false because the checkout itself (payment)
        // was successful, and the application logic expects to handle this case
        // by showing a warning on the next page rather than failing the whole checkout.
        return { orderId, messageSuccess: false };
      });
  } else {
    return { orderId, messageSuccess: true };
  }
};

export const sendMessageThunk = createAsyncThunk(
  'CheckoutPage/sendMessage',
  sendMessagePayloadCreator
);
// Backward compatible wrapper function for sendMessage
export const sendMessage = params => dispatch => {
  return dispatch(sendMessageThunk(params)).unwrap();
};

//////////////////////
// Initiate Inquiry //
//////////////////////

const initiateInquiryPayloadCreator = (
  { inquiryParams, processAlias, transitionName },
  { extra: sdk, rejectWithValue }
) => {
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: inquiryParams?.listingId?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  // Plain inquiry checkout was restored after the bot incident. Signup
  // validation remains in auth APIs, and initiate-privileged still applies
  // listing guards plus inquiry rate limiting before creating the thread.
  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return initiatePrivileged({ isSpeculative: false, orderData: {}, bodyParams, queryParams })
    .then(response => {
      const transactionId = response.data.data.id;
      return transactionId;
    })
    .catch(e => {
      const status = e?.status || e?.statusCode || e?.response?.status || null;
      const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);
      const logFn = isNonCritical ? log.warn : log.error;

      logFn(
        e,
        isNonCritical
          ? 'checkout-inquiry-initiate-failed-non-critical'
          : 'checkout-inquiry-initiate-failed',
        {
          listingId: inquiryParams?.listingId?.uuid || inquiryParams?.listingId || null,
          processAlias,
          transitionName,
        }
      );
      return rejectWithValue(storableError(e));
    });
};

export const initiateInquiryThunk = createAsyncThunk(
  'CheckoutPage/initiateInquiry',
  initiateInquiryPayloadCreator
);
// Backward compatible wrapper function for initiateInquiryWithoutPayment
/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {Object} params
 * @param {Object} params.inquiryParams contains listingId and protectedData
 * @param {String} params.processAlias 'default-inquiry/release-1'
 * @param {String} params.transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (
  inquiryParams,
  processAlias,
  transitionName
) => dispatch => {
  return dispatch(
    initiateInquiryThunk({
      inquiryParams,
      processAlias,
      transitionName,
    })
  ).unwrap();
};

///////////////////////////
// Speculate Transaction //
///////////////////////////
/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */

const speculateTransactionPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, rejectWithValue }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;
  const isPayPalSpeculativeRequestTransition = isPayPalRequestTransition(transitionName);

  const {
    deliveryMethod,
    priceVariantName,
    currency,
    quantity,
    bookingDates,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...(currency ? { currency } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const speculativeEndpoint = isTransition
    ? isPrivilegedTransition
      ? '/api/transition-privileged'
      : 'sdk.transactions.transitionSpeculative'
    : isPrivilegedTransition
    ? isPayPalSpeculativeRequestTransition
      ? '/api/paypal/initiate-spec'
      : '/api/initiate-privileged'
    : 'sdk.transactions.initiateSpeculative';
  shippoDebugLog('CHECKOUT', 'speculateTransactionRequest', {
    transactionId: transactionId?.uuid || transactionId || null,
    listingId: transitionParams?.listingId?.uuid || transitionParams?.listingId || null,
    processAlias: processAlias || null,
    transitionName,
    isPayPalSpeculativeRequestTransition,
    speculativeEndpoint,
    isTransition,
    isPrivilegedTransition,
    speculativeOrderParams: orderParams,
    speculativePayload: {
      isSpeculative: true,
      orderData,
      bodyParams,
      queryParams,
    },
  });

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    shippoDebugLog('CHECKOUT', 'speculateTransactionSuccess', {
      transactionId: tx?.id?.uuid || tx?.id || null,
      lastTransition: tx?.attributes?.lastTransition || null,
      state: tx?.attributes?.state || null,
    });
    return tx;
  };

  const handleError = e => {
    const status = e?.status || e?.statusCode || e?.response?.status || null;
    const isNonCritical = NON_CRITICAL_CHECKOUT_STATUSES.has(status);

    if (!isNetworkError(e)) {
      const logFn = isNonCritical ? log.warn : log.error;
      const errorCode = isNonCritical
        ? 'speculate-transaction-failed-non-critical'
        : 'speculate-transaction-failed';

      logFn(e, errorCode, {
        listingId: transitionParams?.listingId?.uuid || transitionParams?.listingId,
        ...quantityMaybe,
        ...bookingParamsMaybe,
        ...orderData,
        statusText: e.statusText,
        isNonCritical,
      });
    }
    shippoDebugError('CHECKOUT', 'speculateTransactionFailed', e, {
      transactionId: transactionId?.uuid || transactionId || null,
      listingId: transitionParams?.listingId?.uuid || transitionParams?.listingId || null,
      processAlias: processAlias || null,
      transitionName,
      isPayPalSpeculativeRequestTransition,
      speculativeEndpoint,
      isTransition,
      isPrivilegedTransition,
      speculativePayload: {
        isSpeculative: true,
        orderData,
        bodyParams,
        queryParams,
      },
    });
    return rejectWithValue(storableError(e));
  };

  const MAX_RETRIES = 2;
  const speculateWithRetry = async (fn, retriesLeft) => {
    try {
      return await fn();
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return speculateWithRetry(fn, retriesLeft - 1);
      }
      throw e;
    }
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return speculateWithRetry(
      () => transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams }),
      MAX_RETRIES
    )
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    return speculateWithRetry(
      () => guardedTransaction({ action: 'transitionSpeculative', bodyParams, queryParams }),
      MAX_RETRIES
    )
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    const speculativeRequest = () =>
      isPayPalSpeculativeRequestTransition
        ? initiateSpecWithPaypal({ isSpeculative: true, orderData, bodyParams, queryParams })
        : initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams });
    return speculateWithRetry(speculativeRequest, MAX_RETRIES)
      .then(handleSuccess)
      .catch(handleError);
  } else {
    return speculateWithRetry(
      () => guardedTransaction({ action: 'initiateSpeculative', bodyParams, queryParams }),
      MAX_RETRIES
    )
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const speculateTransactionThunk = createAsyncThunk(
  'CheckoutPage/speculateTransaction',
  speculateTransactionPayloadCreator
);
// Backward compatible wrapper function for speculateTransaction
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    speculateTransactionThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

///////////////////////////
// Fetch Stripe Customer //
///////////////////////////
const stripeCustomerPayloadCreator = ({}, { dispatch, rejectWithValue }) => {
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      return response;
    })
    .catch(e => {
      log.error(e, 'checkout-stripe-customer-fetch-failed', {
        include: ['stripeCustomer.defaultPaymentMethod'],
      });
      return rejectWithValue(storableError(e));
    });
};

export const stripeCustomerThunk = createAsyncThunk(
  'CheckoutPage/stripeCustomer',
  stripeCustomerPayloadCreator
);
// Backward compatible wrapper function for stripeCustomer
export const stripeCustomer = () => dispatch => {
  return dispatch(stripeCustomerThunk({})).unwrap();
};

// ================ Slice ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  latestSpeculateTransactionRequestId: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
};

const checkoutPageSlice = createSlice({
  name: 'CheckoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Initiate Order cases
      .addCase(initiateOrderThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(initiateOrderThunk.fulfilled, (state, action) => {
        state.transaction = action.payload;
      })
      .addCase(initiateOrderThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.initiateOrderError = action.payload;
      })
      // Create PayPal Order Id cases
      .addCase(createOrderIdWithPaypalThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(createOrderIdWithPaypalThunk.fulfilled, state => {
        // PayPal order id created successfully, no state change required here.
      })
      .addCase(createOrderIdWithPaypalThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.initiateOrderError = action.payload;
      })
      // Initiate Order with PayPal cases
      .addCase(initiateOrderWithPayPalThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(initiateOrderWithPayPalThunk.fulfilled, (state, action) => {
        state.transaction = action.payload;
      })
      .addCase(initiateOrderWithPayPalThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.initiateOrderError = action.payload;
      })
      // Confirm Payment cases
      .addCase(confirmPaymentThunk.pending, state => {
        state.confirmPaymentError = null;
      })
      .addCase(confirmPaymentThunk.fulfilled, state => {
        // Payment confirmed successfully, no state change needed
      })
      .addCase(confirmPaymentThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.confirmPaymentError = action.payload;
      })
      // Speculate Transaction cases
      .addCase(speculateTransactionThunk.pending, (state, action) => {
        state.speculateTransactionInProgress = true;
        state.speculateTransactionError = null;
        state.latestSpeculateTransactionRequestId = action.meta.requestId;
      })
      .addCase(speculateTransactionThunk.fulfilled, (state, action) => {
        if (state.latestSpeculateTransactionRequestId !== action.meta.requestId) {
          return;
        }

        // Check that the local devices clock is within a minute from the server
        const lastTransitionedAt = action.payload?.attributes?.lastTransitionedAt;
        const localTime = new Date();
        const minute = 60000;
        state.speculateTransactionInProgress = false;
        state.speculatedTransaction = action.payload;
        state.isClockInSync =
          Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute;
      })
      .addCase(speculateTransactionThunk.rejected, (state, action) => {
        if (state.latestSpeculateTransactionRequestId !== action.meta.requestId) {
          return;
        }

        console.error(action.payload); // eslint-disable-line no-console
        state.speculateTransactionInProgress = false;
        state.speculateTransactionError = action.payload;
      })
      // Stripe Customer cases
      .addCase(stripeCustomerThunk.pending, state => {
        state.stripeCustomerFetched = false;
      })
      .addCase(stripeCustomerThunk.fulfilled, state => {
        state.stripeCustomerFetched = true;
      })
      .addCase(stripeCustomerThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.stripeCustomerFetched = true;
        state.stripeCustomerFetchError = action.payload;
      })
      // Initiate Inquiry cases
      .addCase(initiateInquiryThunk.pending, state => {
        state.initiateInquiryInProgress = true;
        state.initiateInquiryError = null;
      })
      .addCase(initiateInquiryThunk.fulfilled, state => {
        state.initiateInquiryInProgress = false;
      })
      .addCase(initiateInquiryThunk.rejected, (state, action) => {
        state.initiateInquiryInProgress = false;
        state.initiateInquiryError = action.payload;
      });
  },
});

// Export the action creators
export const { setInitialValues } = checkoutPageSlice.actions;

// Export the reducer
export default checkoutPageSlice.reducer;
